import { useEffect, useState } from "react";
import styled from "styled-components";

import { savedList, removeSaved } from "../../data/accountApi";

/*
 * The saved properties list on the account page.
 *
 * THIS IS WHAT THE ACCOUNT PAGE USED TO APOLOGISE FOR. It said "Nothing is
 * stored with us yet - saved properties stay on your phone until hosting
 * arrives" from the day it was written, which was honest and also meant the
 * £19.99 subscription was selling saved history that did not exist.
 *
 * EACH ROW LINKS TO THE REPORT IT CAME FROM, and when the property was
 * saved with a valuation that link is the short form carrying the token -
 * so re-opening shows the figure that was on screen when it was saved,
 * without asking a model again. The link is built on the SERVER (see
 * data/savedProperties.js) because the Android app needs the same one.
 */

const Wrap = styled.section`
    margin: 1.5rem 0 0;
`;

const Title = styled.h2`
    margin: 0 0 0.6rem;
    font-size: 1.05rem;
    font-weight: 600;
    color: ${(props) => props.theme.darkblue || "#1b2a41"};
`;

const List = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const Row = styled.li`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
    padding: 0.7rem 0;
    border-top: 1px solid #e8edf2;

    a.where {
        font-weight: 600;
        color: ${(props) => props.theme.darkblue || "#1b2a41"};
    }
`;

const Meta = styled.span`
    font-size: 0.85rem;
    color: #5a6b7b;
`;

const Remove = styled.button`
    margin-left: auto;
    padding: 0.3rem 0.6rem;
    font-size: 0.82rem;
    font-family: inherit;
    color: #8a4040;
    background: transparent;
    border: 1px solid #e3cccc;
    border-radius: ${(props) => props.theme.radius || "4px"};
    cursor: pointer;
    &:hover:not(:disabled) {
        background: #fbf0f0;
    }
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const Quiet = styled.p`
    margin: 0.4rem 0 0;
    font-size: 0.9rem;
    line-height: 1.45;
    color: #5a6b7b;
`;

export default function SavedProperties({ token }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [removing, setRemoving] = useState(null);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;

        savedList(token)
            .then((data) => {
                if (!cancelled) setRows((data && data.saved) || []);
            })
            .catch((err) => {
                if (!cancelled) setError((err && err.message) || "Could not load them just now.");
            });

        return () => {
            cancelled = true;
        };
    }, [token]);

    async function remove(id) {
        setRemoving(id);
        setError(null);
        try {
            await removeSaved(token, id);
            setRows((current) => (current || []).filter((r) => r.id !== id));
        } catch (err) {
            setError((err && err.message) || "Could not remove that just now.");
        } finally {
            setRemoving(null);
        }
    }

    if (!token) return null;

    return (
        <Wrap>
            <Title>Saved properties</Title>

            {error && <Quiet>{error}</Quiet>}

            {rows === null && !error && <Quiet>Loading&hellip;</Quiet>}

            {rows !== null && rows.length === 0 && (
                <Quiet>
                    Nothing saved yet. Open a property report and press &ldquo;Save to my
                    account&rdquo; &mdash; it will be here on any device you sign in on.
                </Quiet>
            )}

            {rows !== null && rows.length > 0 && (
                <List>
                    {rows.map((r) => (
                        <Row key={r.id}>
                            <a className="where" href={r.url}>
                                {r.address || r.postcode}
                            </a>
                            <Meta>
                                {r.address ? r.postcode + " · " : ""}
                                saved{" "}
                                {new Date(r.savedAt).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                })}
                                {/*
                                  * Said out loud, because it is the difference
                                  * between opening this and getting the figure
                                  * back and opening it and being asked fifteen
                                  * questions again.
                                  */}
                                {r.hasValuation ? " · with its valuation" : ""}
                            </Meta>
                            {r.note && <Meta>&ldquo;{r.note}&rdquo;</Meta>}
                            <Remove
                                type="button"
                                onClick={() => remove(r.id)}
                                disabled={removing === r.id}
                            >
                                {removing === r.id ? "Removing…" : "Remove"}
                            </Remove>
                        </Row>
                    ))}
                </List>
            )}
        </Wrap>
    );
}
